"use client";

import Link from "next/link";
import Image from "next/image";
import {navElementClasses} from "./navElementClasses";
import HorizontalNavItems from "./HorizontalNavItems";
import {TOP_LEVEL_MENU_ITEMS} from "@/app/util/navigationMenuConstants";
import clsMerge from "@/app/util/clsMerge";
import useHoverMenus from "./useHoverMenus";
import {
	useCampusNavItem,
	UseCampusNavItemConfig,
} from "@/app/util/useCampusNavItem";
import NavBarAccountSection from "./NavBarAccountSection";
import CampusNavItem from "./CampusNavItem";
import {useTopNavHoverContext} from "../contexts/TopNavHoverContext";
import {useIsLoggedIn} from "../contexts/userInfoContext";
import {Suspense, useMemo} from "react";
import {useCampusContext} from "@/app/_components/contexts/CampusCookieContext";
import CampusLabel, {locationLabelStyles} from "./CampusLabel";
import LoadingLocationLabel from "./LoadingLocationLabel";

interface PrimaryNavProps {
	showNavItems?: boolean;
	className?: string;
}

const PrimaryNav: React.FC<PrimaryNavProps> = ({
	showNavItems = true,
	className,
}) => {
	const isLoggedIn = useIsLoggedIn();
	const uncontrolledHoverHandlers = useHoverMenus();
	const hoverHandlers = useTopNavHoverContext() ?? uncontrolledHoverHandlers;
	const {campus} = useCampusContext();

	const campusNavItemConfig = useMemo<UseCampusNavItemConfig>(() => {
		if (!campus) {
			return {};
		}
		return {
			campusLabelComponent: (
				<Link
					className={locationLabelStyles}
					href={`/campus/${campus.campusId}`}
				>
					<CampusLabel campusName={campus.locationName} />
				</Link>
			),
			loadingComponent: <LoadingLocationLabel />,
		};
	}, [campus]);

	const {campusNavItemProps, isFetchingCampus} =
		useCampusNavItem(campusNavItemConfig);
	return (
		<div
			className={clsMerge(
				"flex w-full items-center justify-between px-4",
				isLoggedIn ? "xl:px-10" : "xl:px-0 xl:ps-10"
			)}
		>
			<Link className="py-2" href="/">
				<div
					className={clsMerge(navElementClasses, "flex h-full items-center")}
				>
					<Image
						className="h-7 w-auto"
						src="/logos/aopsAcademyLogo.svg"
						alt="Art of Problem Solving"
						width="258"
						height="40"
						priority={true}
					/>
				</div>
			</Link>
			{showNavItems && (
				<div
					className={clsMerge("h-full flex-grow items-stretch", className)}
					role="menu"
				>
					<HorizontalNavItems
						classes={{root: "flex-grow"}}
						items={TOP_LEVEL_MENU_ITEMS}
						hoverHandlers={hoverHandlers}
					/>
					<div className="flex items-center gap-1 text-sm">
						{campusNavItemProps && "menuItems" in campusNavItemProps && (
							<CampusNavItem
								{...campusNavItemProps}
								campus={campus}
								isFetchingCampus={isFetchingCampus}
								hasChevron={!isFetchingCampus}
								isOpen={
									hoverHandlers.activeSubmenu === campusNavItemProps.id &&
									hoverHandlers.isSubmenuOpen
								}
								onMouseEnter={hoverHandlers.onDropdownItemMouseEnter(
									campusNavItemProps.id
								)}
								onMouseLeave={hoverHandlers.onDropdownItemMouseLeave}
							/>
						)}
						<Suspense>
							<NavBarAccountSection hoverHandlers={hoverHandlers} />
						</Suspense>
					</div>
				</div>
			)}
		</div>
	);
};

export default PrimaryNav;
